import type { ReactNode } from 'react';
import { formatInches } from '../lib/format.js';

const VIEWBOX_W = 320;
const VIEWBOX_H = 200;
const STROKE = 'var(--color-accent)';
const STROKE_FAINT = 'var(--color-border)';
const LABEL = 'var(--color-text-muted)';
const LABEL_BOLD = 'var(--color-text-primary)';
const TEAL = 'var(--color-measure)';
const PIPE = 'var(--color-diagram-pipe)';
const BLUE = 'var(--color-blue)';

interface DiagramFrameProps {
  children: ReactNode;
  label?: string;
}

function DiagramFrame({ children, label }: DiagramFrameProps) {
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        marginTop: 'var(--space-5)',
      }}
    >
      <svg
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label={label ?? 'Bend diagram'}
      >
        {children}
      </svg>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

interface Point {
  x: number;
  y: number;
}

/** A point `dist` px from `from` along the line toward `to` — used to fillet corners. */
function pointToward(from: Point, to: Point, dist: number): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: from.x + (dx / len) * dist, y: from.y + (dy / len) * dist };
}

function fmtPoint(p: Point): string {
  return `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
}

function formatAngle(angle: number): string {
  return `${Number.isInteger(angle) ? angle.toFixed(0) : angle.toFixed(1)}\u00b0`;
}

export function NinetyDiagram({
  stubHeight,
  markFromEnd,
}: {
  stubHeight: number;
  markFromEnd: number;
}) {
  return (
    <DiagramFrame label="90 degree stub up bend">
      <line
        x1="24"
        y1="158"
        x2="198"
        y2="158"
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M 198,158 Q 222,158 222,134 L 222,42"
        fill="none"
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <line x1="118" y1="143" x2="118" y2="173" stroke={TEAL} strokeWidth="2" />
      <text
        x="118"
        y="190"
        fontSize="11"
        fill={LABEL_BOLD}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
      >
        mark {formatInches(markFromEnd)}
      </text>
      <text x="238" y="100" fontSize="11" fill={LABEL_BOLD} fontFamily="var(--font-mono)">
        {formatInches(stubHeight)}
      </text>
    </DiagramFrame>
  );
}

export function BackToBackDiagram({
  firstMark,
  secondMark,
}: {
  firstMark: number;
  secondMark: number;
}) {
  return (
    <DiagramFrame label="Back to back 90s">
      <path
        d="M 42,42 L 42,136 Q 42,158 64,158 L 256,158 Q 278,158 278,136 L 278,42"
        fill="none"
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <line x1="100" y1="143" x2="100" y2="173" stroke={TEAL} strokeWidth="2" />
      <line x1="220" y1="143" x2="220" y2="173" stroke={TEAL} strokeWidth="2" />
      <text
        x="100"
        y="190"
        fontSize="10"
        fill={LABEL_BOLD}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
      >
        m1 {formatInches(firstMark)}
      </text>
      <text
        x="220"
        y="190"
        fontSize="10"
        fill={LABEL_BOLD}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
      >
        m2 {formatInches(secondMark)}
      </text>
    </DiagramFrame>
  );
}

export function OffsetDiagram({
  rise,
  angle,
  distance,
  fromEnd,
  previousAngle,
}: {
  rise: number;
  angle: number;
  distance: number;
  fromEnd?: number | undefined;
  previousAngle?: number | undefined;
}) {
  const baselineY = 148;
  const bend1X = 118;
  const startX = 26;
  const endX = 296;
  const risePx = clamp(rise * 9, 36, 82);
  const topY = baselineY - risePx;
  const rampDx = clamp(risePx / Math.tan((angle * Math.PI) / 180), 50, 132);
  const bend2X = Math.min(238, bend1X + rampDx);
  const currentPath = `M ${startX},${baselineY} L ${bend1X},${baselineY} L ${bend2X},${topY} L ${endX},${topY}`;
  const ghostRampDx =
    previousAngle && Math.abs(previousAngle - angle) > 0.05
      ? clamp(risePx / Math.tan((previousAngle * Math.PI) / 180), 50, 132)
      : null;
  const ghostPath =
    ghostRampDx === null
      ? null
      : `M ${startX},${baselineY} L ${bend1X},${baselineY} L ${Math.min(
          238,
          bend1X + ghostRampDx,
        )},${topY} L ${endX},${topY}`;
  const mark1 = fromEnd ?? 0;
  const mark2 = mark1 + distance;

  return (
    <DiagramFrame label="2-point offset">
      <defs>
        <filter id="offset-pipe-shadow" x="-10%" y="-20%" width="120%" height="150%">
          <feDropShadow
            dx="0"
            dy="6"
            stdDeviation="3"
            floodColor="var(--color-diagram-shadow)"
            floodOpacity="0.45"
          />
        </filter>
      </defs>
      <rect x="18" y="26" width="284" height="142" rx="10" fill="var(--color-diagram-bg)" />
      {ghostPath && (
        <path
          d={ghostPath}
          fill="none"
          stroke="var(--color-diagram-ghost)"
          strokeWidth="5"
          strokeDasharray="8 8"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.55"
        />
      )}
      <path
        d={currentPath}
        fill="none"
        stroke="var(--color-diagram-pipe-shadow)"
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform="translate(0 7)"
      />
      <path
        d={currentPath}
        fill="none"
        stroke={STROKE}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter="url(#offset-pipe-shadow)"
      />
      <circle
        cx={bend1X}
        cy={baselineY}
        r="5"
        fill="var(--color-diagram-bg)"
        stroke={TEAL}
        strokeWidth="3"
      />
      <circle
        cx={bend2X}
        cy={topY}
        r="5"
        fill="var(--color-diagram-bg)"
        stroke={TEAL}
        strokeWidth="3"
      />
      <line
        x1="268"
        y1={baselineY}
        x2="268"
        y2={topY}
        stroke={TEAL}
        strokeWidth="1.5"
        strokeDasharray="3 4"
      />
      <text
        x="276"
        y={(baselineY + topY) / 2 + 4}
        fontSize="11"
        fill={TEAL}
        fontFamily="var(--font-mono)"
      >
        {formatInches(rise)}
      </text>
      <line
        x1={bend1X}
        y1={baselineY + 22}
        x2={bend2X}
        y2={baselineY + 22}
        stroke={STROKE_FAINT}
        strokeWidth="1.5"
      />
      <text
        x={(bend1X + bend2X) / 2}
        y={baselineY + 39}
        fontSize="10"
        fill={LABEL_BOLD}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
      >
        {formatInches(distance)}
      </text>
      <text
        x={(bend1X + bend2X) / 2 + 8}
        y={topY - 12}
        fontSize="11"
        fill={LABEL_BOLD}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
      >
        {formatAngle(angle)}
      </text>
      <text x="30" y="46" fontSize="10" fill={LABEL} fontFamily="var(--font-ui)">
        mark 1 {formatInches(mark1)} / mark 2 {formatInches(mark2)}
      </text>
    </DiagramFrame>
  );
}

export function Saddle3Diagram({ obstructionHeight }: { obstructionHeight: number }) {
  const baseY = 150;
  const peak = clamp(baseY - obstructionHeight * 12, 36, 132);

  // Conduit vertices: left run, left foot bend, center bend, right foot bend, right run.
  const left = { x: 24, y: baseY };
  const footL = { x: 96, y: baseY };
  const crownTop = { x: 160, y: peak };
  const footR = { x: 224, y: baseY };
  const right = { x: 296, y: baseY };

  // Fillet radius for the three bends — real conduit bends on a radius, not a point.
  const legLength = Math.hypot(crownTop.x - footL.x, crownTop.y - footL.y);
  const r = clamp(legLength * 0.38, 8, 16);

  const beforeFootL = pointToward(footL, left, r);
  const afterFootL = pointToward(footL, crownTop, r);
  const beforeCrown = pointToward(crownTop, footL, r);
  const afterCrown = pointToward(crownTop, footR, r);
  const beforeFootR = pointToward(footR, crownTop, r);
  const afterFootR = pointToward(footR, right, r);

  const fullPath =
    `M ${fmtPoint(left)} L ${fmtPoint(beforeFootL)} Q ${footL.x},${footL.y} ${fmtPoint(afterFootL)}` +
    ` L ${fmtPoint(beforeCrown)} Q ${crownTop.x},${crownTop.y} ${fmtPoint(afterCrown)}` +
    ` L ${fmtPoint(beforeFootR)} Q ${footR.x},${footR.y} ${fmtPoint(afterFootR)} L ${fmtPoint(right)}`;
  // Orange center bend spans the crown plus the upper half of both rise legs.
  const crownSpread = legLength * 0.45;
  const legSplitL = pointToward(footL, crownTop, crownSpread);
  const legSplitR = pointToward(footR, crownTop, crownSpread);
  const leftLeg = `M ${fmtPoint(beforeFootL)} Q ${footL.x},${footL.y} ${fmtPoint(afterFootL)} L ${fmtPoint(legSplitL)}`;
  const crown = `M ${fmtPoint(legSplitL)} L ${fmtPoint(beforeCrown)} Q ${crownTop.x},${crownTop.y} ${fmtPoint(afterCrown)} L ${fmtPoint(legSplitR)}`;
  const rightLeg = `M ${fmtPoint(legSplitR)} L ${fmtPoint(beforeFootR)} Q ${footR.x},${footR.y} ${fmtPoint(afterFootR)}`;

  // Round obstruction (pipe cross-section) the saddle clears, sized to the height.
  const obstructionRadius = clamp(Math.min(obstructionHeight * 6, baseY - peak - 6), 0, 22);
  const showObstruction = obstructionRadius >= 8;

  return (
    <DiagramFrame label="3-point saddle">
      {showObstruction && (
        <>
          <circle
            cx={crownTop.x}
            cy={baseY - 2}
            r={obstructionRadius}
            fill="var(--color-blue-soft)"
            stroke={BLUE}
            strokeWidth="2"
          />
          <circle
            cx={crownTop.x}
            cy={baseY - 2}
            r={Math.max(3, obstructionRadius - 7)}
            fill="none"
            stroke={STROKE_FAINT}
            strokeWidth="1.4"
          />
        </>
      )}
      <path
        d={fullPath}
        fill="none"
        stroke={PIPE}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={leftLeg}
        fill="none"
        stroke={BLUE}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={rightLeg}
        fill="none"
        stroke={BLUE}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={crown}
        fill="none"
        stroke={STROKE}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={footL.x}
        cy={footL.y}
        r="4"
        fill="var(--color-diagram-bg)"
        stroke={TEAL}
        strokeWidth="2.5"
      />
      <circle
        cx={crownTop.x}
        cy={crownTop.y}
        r="4"
        fill="var(--color-diagram-bg)"
        stroke={TEAL}
        strokeWidth="2.5"
      />
      <circle
        cx={footR.x}
        cy={footR.y}
        r="4"
        fill="var(--color-diagram-bg)"
        stroke={TEAL}
        strokeWidth="2.5"
      />
      <text
        x="160"
        y="190"
        fontSize="11"
        fill={LABEL_BOLD}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
      >
        {formatInches(obstructionHeight)}
      </text>
    </DiagramFrame>
  );
}

export function Saddle4Diagram({
  obstructionHeight,
  obstructionWidth,
}: {
  obstructionHeight: number;
  obstructionWidth: number;
}) {
  const top = Math.min(140, Math.max(30, 150 - obstructionHeight * 10));
  const innerW = Math.min(120, Math.max(40, obstructionWidth * 4));
  const x1 = 160 - innerW / 2;
  const x2 = 160 + innerW / 2;
  const rectHeight = Math.max(0, 150 - top - 6);
  return (
    <DiagramFrame label="4-point saddle">
      <line
        x1="24"
        y1="150"
        x2="80"
        y2="150"
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <line
        x1="80"
        y1="150"
        x2={x1}
        y2={top}
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <line
        x1={x1}
        y1={top}
        x2={x2}
        y2={top}
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <line
        x1={x2}
        y1={top}
        x2="240"
        y2="150"
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <line
        x1="240"
        y1="150"
        x2="296"
        y2="150"
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <rect
        x={x1}
        y={top + 6}
        width={Math.max(0, x2 - x1)}
        height={rectHeight}
        fill="none"
        stroke={STROKE_FAINT}
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      <text
        x="160"
        y="176"
        fontSize="11"
        fill={LABEL_BOLD}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
      >
        {formatInches(obstructionWidth)} x {formatInches(obstructionHeight)}
      </text>
    </DiagramFrame>
  );
}

export function KickDiagram({
  stubHeight,
  kickOffset,
}: {
  stubHeight: number;
  kickOffset: number;
}) {
  return (
    <DiagramFrame label="Kick compound 90">
      <line
        x1="24"
        y1="158"
        x2="198"
        y2="158"
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M 198,158 Q 222,158 222,134 L 222,82"
        fill="none"
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <line
        x1="222"
        y1="82"
        x2="246"
        y2={82 - kickOffset * 6}
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <line
        x1="246"
        y1={82 - kickOffset * 6}
        x2="246"
        y2={42 - kickOffset * 6}
        stroke={STROKE}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <text x="262" y="100" fontSize="11" fill={LABEL_BOLD} fontFamily="var(--font-mono)">
        {formatInches(stubHeight)}
      </text>
      <text
        x="262"
        y={82 - (kickOffset * 6) / 2}
        fontSize="10"
        fill={LABEL_BOLD}
        fontFamily="var(--font-mono)"
      >
        kick {formatInches(kickOffset)}
      </text>
    </DiagramFrame>
  );
}

export function RollingOffsetDiagram({
  rise,
  roll,
  trueOffset,
  rollAngle,
}: {
  rise: number;
  roll: number;
  trueOffset: number;
  rollAngle: number;
}) {
  return (
    <DiagramFrame label="Rolling 3D offset">
      <g transform="translate(150 124)">
        <line
          x1="0"
          y1="0"
          x2={Math.min(88, roll * 10)}
          y2="0"
          stroke={STROKE_FAINT}
          strokeWidth="2"
        />
        <line
          x1="0"
          y1="0"
          x2="0"
          y2={-Math.min(88, rise * 10)}
          stroke={STROKE_FAINT}
          strokeWidth="2"
        />
        <line
          x1="0"
          y1="0"
          x2={Math.min(88, roll * 10)}
          y2={-Math.min(88, rise * 10)}
          stroke={STROKE}
          strokeWidth="7"
          strokeLinecap="round"
        />
        <text
          x={Math.min(88, roll * 10) + 8}
          y="6"
          fontSize="10"
          fill={LABEL_BOLD}
          fontFamily="var(--font-mono)"
        >
          roll {formatInches(roll)}
        </text>
        <text
          x="-58"
          y={-Math.min(88, rise * 10) + 12}
          fontSize="10"
          fill={LABEL_BOLD}
          fontFamily="var(--font-mono)"
        >
          rise {formatInches(rise)}
        </text>
        <text x="-100" y="-34" fontSize="11" fill={LABEL_BOLD} fontFamily="var(--font-mono)">
          true {formatInches(trueOffset)}
        </text>
        <text x="-100" y="-16" fontSize="11" fill={LABEL_BOLD} fontFamily="var(--font-mono)">
          roll {formatAngle(rollAngle)}
        </text>
      </g>
    </DiagramFrame>
  );
}

export function ConcentricDiagram({
  innerRadius,
  spacing,
  parallelCount,
  angle,
}: {
  innerRadius: number;
  spacing: number;
  parallelCount: number;
  angle: number;
}) {
  const scale = Math.min(60 / innerRadius, 1.5);
  const cx = 80;
  const cy = 160;
  return (
    <DiagramFrame label="Concentric parallel bends">
      {Array.from({ length: parallelCount }).map((_, i) => {
        const radius = (innerRadius + i * spacing) * scale;
        const angleRad = (angle * Math.PI) / 180;
        const endX = cx + radius * Math.cos(Math.PI - angleRad);
        const endY = cy - radius * Math.sin(Math.PI - angleRad);
        return (
          <path
            key={`concentric-arc-${i}`}
            d={`M ${cx + radius},${cy} A ${radius},${radius} 0 0 0 ${endX},${endY}`}
            fill="none"
            stroke={i === 0 ? STROKE : STROKE_FAINT}
            strokeWidth={i === 0 ? '6' : '3'}
            strokeLinecap="round"
          />
        );
      })}
      <text x="190" y="100" fontSize="11" fill={LABEL_BOLD} fontFamily="var(--font-mono)">
        {parallelCount} parallels
      </text>
      <text x="190" y="120" fontSize="11" fill={LABEL_BOLD} fontFamily="var(--font-mono)">
        radius {formatInches(innerRadius)}
      </text>
      <text x="190" y="140" fontSize="11" fill={LABEL_BOLD} fontFamily="var(--font-mono)">
        spacing {formatInches(spacing)}
      </text>
    </DiagramFrame>
  );
}

export function SegmentedDiagram({
  totalAngle,
  segmentAngle,
  segmentCount,
  chordLength,
}: {
  totalAngle: number;
  segmentAngle: number;
  segmentCount: number;
  chordLength: number;
}) {
  const cx = 160;
  const cy = 170;
  const radius = 80;
  const totalRad = (totalAngle * Math.PI) / 180;
  const segRad = (segmentAngle * Math.PI) / 180;
  const startAngle = Math.PI - totalRad / 2;
  const points: [number, number][] = [];
  for (let i = 0; i <= segmentCount; i++) {
    const a = startAngle + i * segRad;
    points.push([cx + radius * Math.cos(a), cy - radius * Math.sin(a)]);
  }
  const pathD = points.reduce(
    (acc, [x, y], i) => acc + (i === 0 ? `M ${x},${y}` : ` L ${x},${y}`),
    '',
  );
  return (
    <DiagramFrame label="Segmented large-radius bend">
      <path
        d={pathD}
        fill="none"
        stroke={STROKE}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map(([x, y], i) => (
        <circle key={`segment-point-${i}`} cx={x} cy={y} r="2.5" fill={LABEL_BOLD} />
      ))}
      <text
        x="160"
        y="190"
        fontSize="11"
        fill={LABEL_BOLD}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
      >
        {segmentCount} x {formatAngle(segmentAngle)} / chord {formatInches(chordLength)}
      </text>
    </DiagramFrame>
  );
}

export function BendDiagram({
  type,
  result,
  inputs,
  previousAngle,
}: {
  type: string;
  result: unknown;
  inputs: Record<string, number>;
  previousAngle?: number | undefined;
}) {
  if (!result || typeof result !== 'object') return null;
  const r = result as Record<string, unknown>;
  switch (type) {
    case 'ninety':
      return (
        <NinetyDiagram stubHeight={inputs.stubHeight ?? 0} markFromEnd={Number(r.markFromEnd)} />
      );
    case 'back-to-back':
      return (
        <BackToBackDiagram firstMark={Number(r.firstMark)} secondMark={Number(r.secondMark)} />
      );
    case 'offset':
      return (
        <OffsetDiagram
          rise={inputs.rise ?? 0}
          angle={inputs.angle ?? 30}
          distance={Number(r.distanceBetweenMarks)}
          fromEnd={inputs.fromEnd}
          previousAngle={previousAngle}
        />
      );
    case 'saddle3':
      return <Saddle3Diagram obstructionHeight={inputs.obstructionHeight ?? 0} />;
    case 'saddle4':
      return (
        <Saddle4Diagram
          obstructionHeight={inputs.obstructionHeight ?? 0}
          obstructionWidth={inputs.obstructionWidth ?? 0}
        />
      );
    case 'kick':
      return (
        <KickDiagram stubHeight={inputs.stubHeight ?? 0} kickOffset={inputs.kickOffset ?? 0} />
      );
    case 'rolling-offset':
      return (
        <RollingOffsetDiagram
          rise={inputs.rise ?? 0}
          roll={inputs.roll ?? 0}
          trueOffset={Number(r.trueOffset)}
          rollAngle={Number(r.rollAngle)}
        />
      );
    case 'parallel-offset':
      return (
        <OffsetDiagram
          rise={inputs.rise ?? 0}
          angle={inputs.angle ?? 30}
          distance={Number(r.distanceBetweenMarks)}
          fromEnd={inputs.fromEnd}
          previousAngle={previousAngle}
        />
      );
    case 'concentric':
      return (
        <ConcentricDiagram
          innerRadius={inputs.innerRadius ?? 0}
          spacing={inputs.spacing ?? 0}
          parallelCount={inputs.parallelCount ?? 0}
          angle={inputs.angle ?? 90}
        />
      );
    case 'segmented':
      return (
        <SegmentedDiagram
          totalAngle={inputs.totalAngle ?? 90}
          segmentAngle={inputs.segmentAngle ?? 5}
          segmentCount={Number(r.segmentCount)}
          chordLength={Number(r.chordLength)}
        />
      );
    default:
      return null;
  }
}
